import { useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { Button, ButtonLink } from '@carouselgg/nexus/button'
import { ConfirmDialog } from '@carouselgg/nexus/confirm-dialog'
import { DisplayHeading } from '@carouselgg/nexus/display-heading'
import { LogOut, Settings2, Users } from '@carouselgg/nexus/icon'
import { toast } from '@carouselgg/nexus/toast'
import { useAuthStore } from '@carouselgg/auth/store'
import { useMyOrgs, refreshMyOrgs, type MyOrg } from '@carouselgg/auth/use-my-orgs'
import { useMyTeams, refreshMyTeams, type MyTeam } from '@carouselgg/auth/use-my-teams'
import { useMyPrimaryContactUses } from '../../hooks/useMyPrimaryContactUses'
import { leaveOrganization, leaveTeam } from '../../api/organizations'
import styles from './TeamsClubsPage.module.css'

/** Org roles that run an org rather than belong to it; they get Manage. */
const RUNS = new Set(['owner', 'admin', 'manager', 'coach'])
const ROLE_ORDER: Record<string, number> = { owner: 0, admin: 1, manager: 2, coach: 3 }
const ORG_TYPE: Record<string, string> = { organization: 'Club', league: 'League', university: 'University' }
/** What each role can do, said once beside it. */
const ROLE_SCOPE: Record<string, string> = {
  owner: 'Full control, including billing',
  admin: 'Runs everything but billing',
  manager: 'Teams and content, no billing',
  coach: 'Coaches its teams',
}

/**
 * Teams & clubs in the account hub: every roster you're on and every club or
 * league you belong to, with a way to manage what you run and to leave
 * anything you're in. Leaving always confirms first. Owners can't leave
 * until someone else owns the org.
 */
export function TeamsClubsPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const teams = useMyTeams(isAuthenticated)
  const orgs = useMyOrgs(isAuthenticated)
  // One read for every row: which teams am I the primary contact of?
  const contactUses = useMyPrimaryContactUses(isAuthenticated)

  const run = orgs
    .filter((o) => RUNS.has(o.role))
    .sort((a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) || a.name.localeCompare(b.name))
  const memberOf = orgs.filter((o) => !RUNS.has(o.role)).sort((a, b) => a.name.localeCompare(b.name))
  const sortedTeams = [...teams].sort((a, b) => a.gameName.localeCompare(b.gameName) || a.name.localeCompare(b.name))
  const ownsAny = run.some((o) => o.role === 'owner')

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <span className={styles.eyebrow}>Your account</span>
        <DisplayHeading as="h1" size="5xl" className={styles.flat}>Teams &amp; clubs</DisplayHeading>
        <p className={styles.lede}>
          Every roster you&apos;re on and every club or league you belong to. Leaving an organization
          also takes you off all of its teams.
        </p>
      </header>

      {teams.length === 0 && orgs.length === 0 && (
        <p className={styles.empty}>
          You&apos;re not on a team or in a club yet. <Link to="/compete">Find a competition</Link> or{' '}
          <Link to="/orgs">browse clubs and leagues</Link>.
        </p>
      )}

      {teams.length > 0 && (
        <Section id="teams" title="Your teams" count={teams.length} action={<ButtonLink to="/compete" variant="secondary" size="sm">Find a competition</ButtonLink>}>
          {sortedTeams.map((team) => (
            <TeamRow
              key={team.id}
              team={team}
              isPrimaryContact={contactUses.some((u) => u.team_id === team.id)}
            />
          ))}
        </Section>
      )}

      {run.length > 0 && (
        <Section id="run" title="Clubs & leagues you run" count={run.length}>
          {run.map((org) => (
            <OrgRow key={org.slug} org={org} contactTeams={contactUses.filter((u) => u.org_slug === org.slug).map((u) => u.team_name)} />
          ))}
          {ownsAny && (
            <li className={styles.note}>To leave a club or league you own, make someone else its owner first.</li>
          )}
        </Section>
      )}

      {memberOf.length > 0 && (
        <Section id="member" title="Clubs & leagues you're in" count={memberOf.length}>
          {memberOf.map((org) => (
            <OrgRow key={org.slug} org={org} contactTeams={contactUses.filter((u) => u.org_slug === org.slug).map((u) => u.team_name)} />
          ))}
        </Section>
      )}
    </div>
  )
}

function Section({ id, title, count, action, children }: {
  id: string
  title: string
  count: number
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section className={styles.section} aria-labelledby={`${id}-title`}>
      <div className={styles.sectionHead}>
        <DisplayHeading as="h2" size="3xl" id={`${id}-title`} className={styles.flat}>{title}</DisplayHeading>
        <span className={styles.count}>{count}</span>
        {action && <span className={styles.sectionAction}>{action}</span>}
      </div>
      <ul className={styles.rows}>{children}</ul>
    </section>
  )
}

function Logo({ src, name }: { src?: string; name: string }) {
  return (
    <span className={styles.logo} aria-hidden="true">
      {src ? <img src={src} alt="" /> : name.charAt(0).toUpperCase()}
    </span>
  )
}

function title(role: string): string {
  const words = role.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function TeamRow({ team, isPrimaryContact }: { team: MyTeam; isPrimaryContact: boolean }) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const isCaptain = team.role === 'captain'

  async function handleLeave() {
    setLeaving(true)
    try {
      await leaveTeam(team.orgSlug, team.id)
      toast.success(`You left ${team.name}`)
      refreshMyTeams()
    } catch {
      toast.error('Could not leave the team. Please try again.')
    } finally {
      setLeaving(false)
    }
  }

  return (
    <li className={styles.row}>
      <Logo src={team.logoUrl || team.orgLogoUrl} name={team.name} />
      <span className={styles.who}>
        <Link to={`/org/${team.orgSlug}/teams/${team.slug}`} className={styles.name}>{team.name}</Link>
        <span className={styles.meta}>
          {team.orgName} · {team.gameName}
          {team.ownerRequestPending && <> · Asked to own</>}
          {team.ownerRequestable && !team.ownerRequestPending && <> · No owner yet</>}
          <span className={styles.roleInline}> · {title(team.role)}</span>
        </span>
      </span>
      <span className={styles.role}>{title(team.role)}</span>
      <span className={styles.actions}>
        {isCaptain && (
          <ButtonLink to={`/org/${team.orgSlug}/manage/teams/${team.id}`} variant="secondary" size="sm">
            <Users size={14} aria-hidden="true" /> Manage roster
          </ButtonLink>
        )}
        <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(true)} disabled={leaving} aria-label={`Leave ${team.name}`}>
          Leave
        </Button>
      </span>
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleLeave}
        title={`Leave ${team.name}?`}
        description={
          isCaptain
            ? 'You\'ll be removed from this team\'s roster. You\'re a captain: if no other captains remain, the team has no captain until the organization assigns one.'
            : 'You\'ll be removed from this team\'s roster. A team or org admin can add you back later.'
        }
        children={isPrimaryContact ? (
          <p className={styles.dialogNote}>
            You&apos;re {team.name}&apos;s primary contact. When you leave, it passes to another captain leagues can reach, or the team has none until one is chosen.
          </p>
        ) : undefined}
        confirmLabel="Leave team"
        confirmIcon={<LogOut size={16} aria-hidden="true" />}
        variant="danger"
      />
    </li>
  )
}

function OrgRow({ org, contactTeams }: { org: MyOrg; contactTeams: string[] }) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const runs = RUNS.has(org.role)
  const canLeave = org.role !== 'owner'
  const kind = ORG_TYPE[org.type] ?? 'Organization'

  async function handleLeave() {
    setLeaving(true)
    try {
      await leaveOrganization(org.slug)
      toast.success(`You left ${org.name}`)
      refreshMyOrgs()
      refreshMyTeams()
    } catch {
      toast.error('Could not leave the organization. Please try again.')
    } finally {
      setLeaving(false)
    }
  }

  return (
    <li className={styles.row}>
      <Logo src={org.logoUrl} name={org.name} />
      <span className={styles.who}>
        <Link to={`/org/${org.slug}`} className={styles.name}>{org.name}</Link>
        <span className={styles.meta}>
          {kind}{org.isTeamCaptain && ' · you captain a team here'}
          <span className={styles.roleInline}> · {title(org.role)}</span>
        </span>
      </span>
      <span className={styles.role}>
        {title(org.role)}
        {ROLE_SCOPE[org.role] && <span className={styles.scope}>{ROLE_SCOPE[org.role]}</span>}
      </span>
      <span className={styles.actions}>
        {runs && (
          <ButtonLink to={`/org/${org.slug}/manage`} variant="secondary" size="sm">
            <Settings2 size={14} aria-hidden="true" /> Manage
          </ButtonLink>
        )}
        {canLeave && (
          <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(true)} disabled={leaving} aria-label={`Leave ${org.name}`}>
            Leave
          </Button>
        )}
      </span>
      {canLeave && (
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleLeave}
          title={`Leave ${org.name}?`}
          description="You'll be removed from this organization and all of its teams. You can be invited back later."
          children={contactTeams.length > 0 ? (
            <p className={styles.dialogNote}>
              You&apos;re the primary contact for {contactTeams.join(', ')}. When you leave, it passes to another captain leagues can reach, or the team has none until one is chosen.
            </p>
          ) : undefined}
          confirmLabel="Leave organization"
          confirmIcon={<LogOut size={16} aria-hidden="true" />}
          variant="danger"
        />
      )}
    </li>
  )
}
